import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useState } from 'react';
import { Calendar } from 'lucide-react';

interface EnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EnrollmentModal({ isOpen, onClose }: EnrollmentModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    childName: '',
    childDob: '',
    startCare: '',
    comments: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Enrollment form submitted:', formData);
    // Reset form and close modal
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      childName: '',
      childDob: '',
      startCare: '',
      comments: ''
    });
    onClose();
    alert('Thank you for your interest! We\'ll contact you soon to schedule your tour.');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader className="pb-6">
          <DialogTitle className="text-2xl font-bold text-gray-800">Schedule a Tour</DialogTitle>
          <DialogDescription className="text-gray-600">
            Fill out the form below to schedule a tour of our facility and learn more about our programs.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First Name and Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                placeholder="First Name"
                className="w-full border-0 border-b-2 border-gray-300 rounded-none bg-transparent px-0 py-2 focus:border-orange-400 focus:ring-0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                placeholder="Last Name"
                className="w-full border-0 border-b-2 border-gray-300 rounded-none bg-transparent px-0 py-2 focus:border-orange-400 focus:ring-0"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full border-0 border-b-2 border-gray-300 rounded-none bg-transparent px-0 py-2 focus:border-orange-400 focus:ring-0"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone <span className="text-red-500">*</span>
            </label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="w-full border-0 border-b-2 border-gray-300 rounded-none bg-transparent px-0 py-2 focus:border-orange-400 focus:ring-0"
            />
          </div>

          {/* Name of Child */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name of Child <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              name="childName"
              value={formData.childName}
              onChange={handleInputChange}
              required
              className="w-full border-0 border-b-2 border-gray-300 rounded-none bg-transparent px-0 py-2 focus:border-orange-400 focus:ring-0"
            />
          </div>

          {/* Child Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Child Date of Birth <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                type="date"
                name="childDob"
                value={formData.childDob}
                onChange={handleInputChange}
                required
                className="w-full border-0 border-b-2 border-gray-300 rounded-none bg-transparent px-0 py-2 focus:border-orange-400 focus:ring-0"
              />
              <Calendar className="absolute right-0 top-2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* When do you want to start care */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              When do you want to start care? <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                type="date"
                name="startCare"
                value={formData.startCare}
                onChange={handleInputChange}
                required
                className="w-full border-0 border-b-2 border-gray-300 rounded-none bg-transparent px-0 py-2 focus:border-orange-400 focus:ring-0"
              />
              <Calendar className="absolute right-0 top-2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Additional Comments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Comments/Questions
            </label>
            <Textarea
              name="comments"
              value={formData.comments}
              onChange={handleInputChange}
              rows={4}
              className="w-full border-0 border-b-2 border-gray-300 rounded-none bg-transparent px-0 py-2 focus:border-orange-400 focus:ring-0 resize-none"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6">
            <Button
              type="submit"
              className="bg-black hover:bg-gray-800 text-white px-8 py-3 rounded-full text-base font-medium"
            >
              Send
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}